import {isFeatureEnabled} from 'feature-flags';
import {Flag, Rank, Separator, Tooltip} from 'platform/components';
import {
  Box,
  Heading,
  HStack,
  Icon,
  Right,
  Scroll,
  Show,
  Text,
  ThemeColorTextPath,
  VStack,
} from 'platform/foundation';
import {useFormatCurrency, useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {memo} from 'react';

import {isNotNil} from 'ramda';

import {SourcingVehicleDetailResponseBody} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import {i18n} from '@dms/i18n';
import {ColorCircle, CURRENCY_CZK} from '@dms/shared';

import {
  CurrencyCodeType,
  EMPTY_PLACEHOLDER,
  getSafely,
  Nullish,
  parseDate,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

import {ActionButtonsVehicle} from '../../../components/ActionButtonsVehicle/ActionButtonsVehicle';
import {Clamp} from '../../../components/Clamp/Clamp';
import {CountryFlag} from '../../../components/CountryFlag/CountryFlag';
import {HitlistBlacklistFlag} from '../../../components/HitlistBlacklistFlag/HitlistBlacklistFlag';
import {PriceChangesPopper} from '../../../components/PriceChangesPopper/PriceChangesPopper';
import {VehicleImage} from '../../../components/VehicleImage/VehicleImage';
import {useApiEnums} from '../../../hooks/useApiEnums';
import {FeaturesType} from '../../../types/FeaturesType';
import {decimalToPercentRank} from '../../../utils/decimalToPercentRank';
import {getDetailFirstLine, getDetailFirstLineForTooltip} from '../../../utils/getDetailFirstLine';
import {getVehicleDetailPosition} from '../../../utils/getVehicleDetailPosition';
import {isAuctionVehicle} from '../../../utils/isAuctionVehicle';
import {MinMaxValues} from '../utils/getMinMaxValues';
import {ComparisonBlock} from './ComparisonBlock';
import {ComparisonRow} from './ComparisonRow';

export interface VehicleColumnProps extends TestIdProps {
  vehicle: SourcingVehicleDetailResponseBody;
  minMaxValues: MinMaxValues;
}

const VehicleColumnComponent = (props: VehicleColumnProps) => {
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();
  const {getEnum} = useApiEnums();
  const colorEnum = getEnum('body_color');

  const {sourcingVehicle, summary, vehicleSummary} = props.vehicle;
  const {minPrice, maxPrice, minMileage, maxMileage, minYear, maxYear} = props.minMaxValues;

  const vehiclePosition = getVehicleDetailPosition(
    summary.position ?? sourcingVehicle.extraData?.marketPosition,
    summary.positionFrom ?? sourcingVehicle.extraData?.marketPositionCount,
    sourcingVehicle.pricePercentile
  );
  const originalPrice = sourcingVehicle.price?.originalWithVat;
  const originalCurrency = sourcingVehicle.price?.originalCurrency;
  const currency = sourcingVehicle.price?.currency;
  const originalCurrencyCode = originalCurrency?.key as CurrencyCodeType;
  const convertedPrice = summary.original;
  const convertedCurrencyCode = currency?.key as CurrencyCodeType;
  const firstScrapedPrice = getSafely(sourcingVehicle.priceChanges, 0) ?? convertedPrice;
  const priceColor = match<number | Nullish, ThemeColorTextPath | undefined>(
    maxPrice !== minPrice ? originalPrice : undefined
  )
    .with(maxPrice, () => 'danger')
    .with(minPrice, () => 'success')
    .otherwise(() => undefined);
  const mileageColor = match<number | Nullish, ThemeColorTextPath | undefined>(
    maxMileage !== minMileage ? sourcingVehicle.mileage : undefined
  )
    .with(maxMileage, () => 'danger')
    .with(minMileage, () => 'success')
    .otherwise(() => undefined);
  const yearColor = match<number | Nullish, ThemeColorTextPath | undefined>(
    maxYear !== minYear ? sourcingVehicle.year : undefined
  )
    .with(minYear, () => 'danger')
    .with(maxYear, () => 'success')
    .otherwise(() => undefined);
  const color = colorEnum.find((color) => color?.key === sourcingVehicle.color?.color?.key);
  const highlightedOrPremiumFeatures = Object.values(props.vehicle.features ?? {}).filter(
    ({isAvailable, isHighlight}: FeaturesType) => isAvailable && isHighlight
  );
  const suspiciousReasons = sourcingVehicle?.suspiciousReason
    .map((reason) => i18n.t(`entity.vehicle.labels.${reason}`))
    .join(', ');

  return (
    <>
      <Box position="sticky" top={0} backgroundColor="general.white">
        <VehicleImage vehicle={props.vehicle} />
        <Box padding={2}>
          <VStack spacing={2}>
            <Right>
              <ActionButtonsVehicle
                vehicle={props.vehicle}
                isDisabled={!sourcingVehicle.isAvailable}
                data-testid={suffixTestId('actionButtonsVehicle', props)}
              />
            </Right>
            <Clamp lines={3}>
              <Box height={15}>
                <FloatLeft>
                  <Box paddingRight={1}>
                    <Show when={vehicleSummary?.comment}>
                      <Tooltip placement="top" label={vehicleSummary?.comment}>
                        <Icon size={4} value="communication/comment" />
                      </Tooltip>
                    </Show>
                  </Box>
                </FloatLeft>
                <FloatRight>
                  <Box height={5}>
                    <HStack height="100%" align="center" spacing={1}>
                      <Show
                        whenFeatureEnabled={featureFlags.SOURCING_SUSPICIOUS_VEHICLES}
                        when={sourcingVehicle.suspicious}
                      >
                        <Tooltip label={suspiciousReasons}>
                          <Icon value="alert/warning" color="severity.warning" size={5} />
                        </Tooltip>
                      </Show>
                      <HitlistBlacklistFlag sourcingVehicle={sourcingVehicle} />
                    </HStack>
                  </Box>
                </FloatRight>
                <Tooltip label={getDetailFirstLineForTooltip(sourcingVehicle)}>
                  <Heading size={5}>{getDetailFirstLine(sourcingVehicle)}</Heading>
                </Tooltip>
              </Box>
            </Clamp>
          </VStack>
        </Box>
        <Separator spacing={0} />
      </Box>
      <ComparisonBlock>
        <ComparisonRow>
          <Text size="xSmall">
            {vehiclePosition[0]} {vehiclePosition[1]}
          </Text>
        </ComparisonRow>
        <ComparisonRow>
          <Box height={isFeatureEnabled(featureFlags.SOURCING_PRICE_CHANGES) ? 9 : undefined}>
            <VStack spacing={1}>
              <Show
                when={firstScrapedPrice !== convertedPrice}
                whenFeatureEnabled={featureFlags.SOURCING_PRICE_CHANGES}
              >
                <HStack spacing={1} align="center">
                  <PriceChangesPopper
                    vehicle={sourcingVehicle}
                    currentPrice={convertedPrice ?? 0}
                    currency={convertedCurrencyCode}
                    popperPlacement="top-start"
                    usePortal
                  />
                  <Text size="xxSmall" color="tertiary" strikethrough>
                    {formatCurrency(firstScrapedPrice ?? 0, convertedCurrencyCode)}
                  </Text>
                </HStack>
              </Show>
              <HStack spacing={1} align="center">
                <Show when={sourcingVehicle.price?.vatReclaimable}>
                  <Tooltip
                    placement="top"
                    label={i18n.t('entity.vehicle.labels.tooltipVatReclaimable')}
                  >
                    <Flag
                      label={i18n.t('entity.warehouse.labels.articleVat')}
                      colorScheme="black"
                      size="small"
                    />
                  </Tooltip>
                </Show>
                <Tooltip
                  placement="top"
                  label={
                    isNotNil(originalPrice)
                      ? formatCurrency(originalPrice, originalCurrencyCode)
                      : undefined
                  }
                >
                  <Text color={priceColor} size="xSmall">
                    {formatCurrency(convertedPrice ?? 0, convertedCurrencyCode)}
                  </Text>
                </Tooltip>
              </HStack>
            </VStack>
          </Box>
        </ComparisonRow>
        <ComparisonRow>
          <HStack spacing={2} align="center">
            <Rank percentage={(summary.rank ?? 0) * 100} />
            <Text size="xSmall">{decimalToPercentRank(summary.rank ?? undefined)}</Text>
          </HStack>
        </ComparisonRow>
      </ComparisonBlock>
      <Separator spacing={0} />
      <ComparisonBlock>
        <ComparisonRow>
          <Text size="xSmall">
            {isAuctionVehicle(props.vehicle) && props.vehicle.auctionVehicle.registrationDate
              ? formatDateTime(
                  'dateShort',
                  parseDate(props.vehicle.auctionVehicle.registrationDate)
                )
              : EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">
            {sourcingVehicle.technicalInspectionValidTo
              ? formatDateTime('dateShort', parseDate(sourcingVehicle?.technicalInspectionValidTo))
              : EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">
            {sourcingVehicle.features?.some((feature) => feature.key === 'FEATURE_SERVICE_BOOK')
              ? i18n.t('general.labels.yes')
              : i18n.t('general.labels.no')}
          </Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall" color={mileageColor}>
            {isNotNil(sourcingVehicle.mileage)
              ? `${formatNumber(sourcingVehicle.mileage, 0)} ${i18n.t('general.metric.km')}`
              : EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text color={yearColor} size="xSmall">
            {sourcingVehicle.year ?? EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow>
          <HStack spacing={1} align="center">
            <CountryFlag
              size={4}
              country={sourcingVehicle.originCountry?.key ?? sourcingVehicle.seller?.country?.key}
            />
            <Text size="xSmall" noWrap>
              {sourcingVehicle.originCountry?.translation ??
                sourcingVehicle.seller?.country?.translation ??
                EMPTY_PLACEHOLDER}
            </Text>
          </HStack>
        </ComparisonRow>
      </ComparisonBlock>
      <Separator spacing={0} />
      <ComparisonBlock>
        <ComparisonRow>
          <Text size="xSmall">{sourcingVehicle.fuelType?.translation ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">{sourcingVehicle.power ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">
            {formatNumber(sourcingVehicle.cubicCapacity, 0) ?? EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">
            {sourcingVehicle.transmission?.translation ?? EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">{sourcingVehicle.driveType?.translation ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
      </ComparisonBlock>
      <Separator spacing={0} />
      <ComparisonBlock>
        <ComparisonRow>
          <Text size="xSmall">
            {formatCurrency(
              summary.original ?? 0,
              sourcingVehicle.price?.currency?.key ?? CURRENCY_CZK
            )}
          </Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">
            {isNotNil(sourcingVehicle.extraData?.predictedPrice)
              ? formatCurrency(
                  sourcingVehicle.extraData?.predictedPrice,
                  sourcingVehicle.price?.currency?.key ?? CURRENCY_CZK
                )
              : EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text
            size="xSmall"
            color={(sourcingVehicle.maximalPossibleMargin ?? 0) < 0 ? 'danger' : undefined}
          >
            {isNotNil(sourcingVehicle.maximalPossibleMargin)
              ? formatCurrency(
                  sourcingVehicle.maximalPossibleMargin,
                  sourcingVehicle.price?.currency?.key ?? CURRENCY_CZK
                )
              : EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">
            {isNotNil(summary.vehicleCosts)
              ? formatCurrency(
                  summary.vehicleCosts,
                  sourcingVehicle.price?.currency?.key ?? CURRENCY_CZK
                )
              : EMPTY_PLACEHOLDER}
          </Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">{summary.lid ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">{sourcingVehicle.daysOnStock ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
      </ComparisonBlock>
      <Separator spacing={0} />
      <ComparisonBlock>
        <ComparisonRow>
          <HStack align="center" spacing={1}>
            {sourcingVehicle.color?.color?.key ? (
              <ColorCircle
                colorLabel={color?.label}
                colorCode={color?.colorCode}
                needBorder={sourcingVehicle.color.color.key === 'COLOR_WHITE'}
                isDisabled
              />
            ) : null}

            <Text size="xSmall">
              {sourcingVehicle.color?.color?.translation ?? EMPTY_PLACEHOLDER}
            </Text>
          </HStack>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">{sourcingVehicle.numberOfSeats ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">{sourcingVehicle.carStyle?.translation ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
        <ComparisonRow>
          <Text size="xSmall">{sourcingVehicle.doorCount?.translation ?? EMPTY_PLACEHOLDER}</Text>
        </ComparisonRow>
      </ComparisonBlock>
      <Separator spacing={0} />
      <Scroll auto height={48}>
        <ComparisonBlock hasNoSpace>
          {highlightedOrPremiumFeatures.map((feature) => (
            <ComparisonRow key={feature.key}>
              <Tooltip label={feature.translation}>
                <Text size="xSmall" color={feature.isHighlight ? 'link' : undefined} noWrap>
                  {feature.translation}
                </Text>
              </Tooltip>
            </ComparisonRow>
          ))}
        </ComparisonBlock>
      </Scroll>
      <ComparisonBlock hasNoSpace>
        {Object.values(props.vehicle.features ?? {}).map((feature: FeaturesType) => (
          <ComparisonRow key={feature.key}>
            <Tooltip label={feature.translation}>
              <Text
                size="xSmall"
                color={match<FeaturesType, ThemeColorTextPath | undefined>(feature)
                  .with({isAvailable: false}, () => 'tertiary')
                  .with({isHighlight: true}, () => 'link')
                  .otherwise(() => undefined)}
                noWrap
              >
                {feature.translation}
              </Text>
            </Tooltip>
          </ComparisonRow>
        ))}
      </ComparisonBlock>
      <Separator spacing={0} />
    </>
  );
};

export const VehicleColumn = memo(VehicleColumnComponent);

const FloatLeft = styled.div`
  float: left;
`;
const FloatRight = styled.div`
  float: right;
  height: 100%;
  display: flex;
  align-items: flex-end;
  shape-outside: inset(calc(100% - ${({theme}) => theme.getSize(5)}) 0 0);
`;
